import React, { useEffect, useState } from 'react';
import { getAnalytics, getAnalyticsDetails, getAnalyticsUsers } from '../../api/analytics';
import { getToday, getPresetDates } from '../../utils/analyticsHelpers';
import '../../components/analytics/sections/analyticsDashboard.css';
import DateFilterBar from '../../components/analytics/sections/DateFilterBar';
import OverviewCards from '../../components/analytics/sections/OverviewCards';
import DailyActivity from '../../components/analytics/sections/DailyActivity';
import BookingFunnel from '../../components/analytics/sections/BookingFunnel';
import PaymentOverview from '../../components/analytics/sections/PaymentOverview';
import Conversion from '../../components/analytics/sections/Conversion';
import MostViewedTrips from '../../components/analytics/MostViewedTrips';
import SearchInsights from '../../components/analytics/SearchInsights';
import UsersVisitors from '../../components/analytics/usersVisitors/UsersVisitors';

// Keep this in sync with the page size UsersVisitors/Pagination use -
// it's the "first page" fetched here and handed down as a prop so that
// component doesn't have to re-fetch the same data on mount.
const USERS_PAGE_SIZE = 20;

const EMPTY_USERS_DATA = {
    summary: {
        uniqueUsers: 0,
        loggedInUsers: 0,
        anonymousVisitors: 0,
        totalVisitors: 0
    },
    users: []
};

const SupAnalytics = () => {
    const today = getToday();

    const [datePreset, setDatePreset] = useState('today');

    const [from, setFrom] = useState(today);
    const [to, setTo] = useState(today);

    const [analytics, setAnalytics] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [details, setDetails] = useState({
        topTrips: [],
        topDestinations: [],
        tripTypes: []
    });
    const [usersData, setUsersData] = useState(EMPTY_USERS_DATA);

    const { users = 0, sessions = 0, searches = 0, tripViews = 0, funnel = {}, daily = [] } = analytics;

    // -----------------------------------------
    // Fetch overview numbers
    // -----------------------------------------

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await getAnalytics({
                from,
                to
            });

            const responseBody = response?.data ?? response ?? null;

            if (!responseBody) {
                throw new Error('Analytics response is empty');
            }

            const analyticsData = responseBody?.data ?? responseBody;

            setAnalytics(analyticsData || {});
        } catch (err) {
            console.error('Analytics fetch error:', err);

            setError(err?.response?.data?.message || err?.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    // -----------------------------------------
    // Fetch top trips / destinations / trip types
    // -----------------------------------------

    const fetchAnalyticsDetails = async () => {
        try {
            const response = await getAnalyticsDetails({
                from,
                to
            });

            const responseBody = response?.data ?? response ?? null;

            if (!responseBody) {
                throw new Error('Analytics details response is empty');
            }

            const detailsData = responseBody?.data ?? responseBody;

            setDetails({
                topTrips: detailsData?.topTrips || [],

                topDestinations: detailsData?.topDestinations || [],

                tripTypes: detailsData?.tripTypes || []
            });
        } catch (err) {
            console.error('Analytics details fetch error:', err);

            setDetails({ topTrips: [], topDestinations: [], tripTypes: [] });
        }
    };

    // -----------------------------------------
    // Fetch the first page of users/visitors. UsersVisitors
    // seeds itself from this and only fetches again when the
    // person pages past this first page.
    // -----------------------------------------

    const fetchAnalyticsUsers = async () => {
        try {
            const response = await getAnalyticsUsers({
                from,
                to,
                limit: USERS_PAGE_SIZE,
                offset: 0
            });

            const responseBody = response?.data;

            if (!responseBody?.status) {
                throw new Error(responseBody?.message || 'Failed to fetch users analytics');
            }

            setUsersData(responseBody.data);
        } catch (error) {
            console.error('Failed to fetch users analytics:', error);

            setUsersData(EMPTY_USERS_DATA);
        }
    };

    useEffect(() => {
        if (!from || !to) {
            return;
        }

        fetchAnalytics();
        fetchAnalyticsDetails();
        fetchAnalyticsUsers();
    }, [from, to]);

    const handleDatePreset = (preset) => {
        if (preset === 'custom') {
            setDatePreset('custom');
            return;
        }

        const dates = getPresetDates(preset);

        if (!dates) {
            return;
        }

        setDatePreset(preset);
        setFrom(dates.from);
        setTo(dates.to);
    };

    const handleFromChange = (event) => {
        const value = event.target.value;

        setDatePreset('custom');
        setFrom(value);

        if (value > to) {
            setTo(value);
        }
    };

    const handleToChange = (event) => {
        const value = event.target.value;

        setDatePreset('custom');
        setTo(value);
    };

    return (
        <div className='analyticsPage'>
            {/* ------------------------------------------------ */}
            {/* Header */}
            {/* ------------------------------------------------ */}

            <div className='analyticsHeader'>
                <div>
                    <h1>Analytics</h1>

                    <p>Track users, activity, bookings and payments</p>
                </div>

                <DateFilterBar
                    datePreset={datePreset}
                    from={from}
                    to={to}
                    today={today}
                    onPresetChange={handleDatePreset}
                    onFromChange={handleFromChange}
                    onToChange={handleToChange}
                />
            </div>

            {/* ------------------------------------------------ */}
            {/* Error / Loading */}
            {/* ------------------------------------------------ */}

            {error && <div className='analyticsError'>{error}</div>}

            {loading && <div className='analyticsLoading'>Loading analytics...</div>}

            {/* ------------------------------------------------ */}
            {/* Overview Cards */}
            {/* ------------------------------------------------ */}

            <OverviewCards
                cards={[
                    { label: 'Users', value: users },
                    { label: 'Sessions', value: sessions },
                    { label: 'Searches', value: searches },
                    { label: 'Trip Views', value: tripViews }
                ]}
            />

            {/* ------------------------------------------------ */}
            {/* Dashboard Grid */}
            {/* ------------------------------------------------ */}

            <div className='analyticsDashboardGrid'>
                <DailyActivity daily={daily} />

                <BookingFunnel tripViews={tripViews} funnel={funnel} />

                <PaymentOverview
                    paymentInitiated={funnel.paymentInitiated}
                    paymentSuccess={funnel.paymentSuccess}
                    paymentFailed={funnel.paymentFailed}
                    bookingConfirmed={funnel.bookingConfirmed}
                />

                <Conversion tripViews={tripViews} funnel={funnel} />

                <MostViewedTrips trips={details.topTrips} />

                <SearchInsights destinations={details.topDestinations} tripTypes={details.tripTypes} />

                <UsersVisitors data={usersData} from={from} to={to} />
            </div>
        </div>
    );
};

export default SupAnalytics;
